import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  Tooltip,
  Switch,
  FormControlLabel
} from '@mui/material';
import { Add as AddIcon, Clear as ClearIcon, FilterAltOffOutlined, Search as SearchIcon } from '@mui/icons-material';
import codexService from '../services/codexService';
import codexTypeService from '../services/codexTypeService';
import useListState from '../hooks/useListState';
import { STATUS_CONFIG, getStatusConfig } from '../config/statusConfig';
import useCan from '../hooks/useCan';
import CodexThumbnailCard from '../components/CodexThumbnailCard';
import DocumentCreateDialog from '../components/DocumentCreateDialog';
import {
  createPreviewFromBlob,
  generatePdfThumbnail,
  dataUrlToObjectUrl,
} from '../utils/codexPreviewUtils';

const CACHE_STORAGE_KEY = 'documentiPreviewCache';
const CACHE_STORAGE_PREFIX = `${CACHE_STORAGE_KEY}:`;

const resolvePreviewStorage = () => {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    return window.localStorage;
  } catch (error) {
    console.warn('Local storage non disponibile', error);
    return null;
  }
};

const DEFAULT_PAGE_SIZE = 12;

const MAX_CACHED_PREVIEWS = 12; // Keep localStorage usage well below per-origin quota (~5MB)

const Documenti = () => {
  const can = useCan();
  const navigate = useNavigate();
  const [codexCards, setCodexCards] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [codexTypes, setCodexTypes] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [typeFilter, setTypeFilter] = useState('');
  const [searchInAttributes, setSearchInAttributes] = useState(false);
  const previewUrlsRef = useRef(new Set());
  const previewGenerationRef = useRef(Date.now());
  const previewCacheRef = useRef(new Map());
  const cacheLoadedRef = useRef(false);
  const previewStorageRef = useRef(undefined);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [successMessage, setSuccessMessage] = useState(null);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const sentinelRef = useRef(null);
  const sentinelVisibleRef = useRef(false);
  const pollingCancelRef = useRef(null);
  const loadingStateRef = useRef(false);

  useEffect(() => {
    loadingStateRef.current = loading || isFetchingMore;
  }, [loading, isFetchingMore]);

  const getPreviewStorage = useCallback(() => {
    if (previewStorageRef.current !== undefined) {
      return previewStorageRef.current;
    }
    previewStorageRef.current = resolvePreviewStorage();
    return previewStorageRef.current;
  }, []);

  if (!cacheLoadedRef.current) {
    const storage = getPreviewStorage();
    try {
      const cacheMap = new Map();
      if (storage) {
        const keysToProcess = [];
        for (let index = 0; index < storage.length; index += 1) {
          const key = storage.key(index);
          if (key?.startsWith(CACHE_STORAGE_PREFIX)) {
            keysToProcess.push(key);
          }
        }

        keysToProcess.forEach((key) => {
          const payload = storage.getItem(key);
          if (!payload) {
            return;
          }
          try {
            const parsed = JSON.parse(payload);
            if (!parsed?.dataUrl) {
              return;
            }
            const id = key.slice(CACHE_STORAGE_PREFIX.length);
            if (id) {
              cacheMap.set(id, {
                dataUrl: parsed.dataUrl,
                mimeType: parsed.mimeType,
                fileName: parsed.fileName,
                storedAt: parsed.storedAt ?? Date.now(),
              });
            }
          } catch (parseError) {
            console.warn(`Cache anteprima malformata per la chiave ${key}`, parseError);
          }
        });
      }
      previewCacheRef.current = cacheMap;

      if (previewCacheRef.current.size > MAX_CACHED_PREVIEWS) {
        const entriesByAge = Array.from(previewCacheRef.current.entries()).sort(
          (a, b) => (a[1]?.storedAt ?? 0) - (b[1]?.storedAt ?? 0)
        );
        const overflow = entriesByAge.length - MAX_CACHED_PREVIEWS;
        for (let index = 0; index < overflow; index += 1) {
          const [idToRemove, removed] = entriesByAge[index];
          if (!idToRemove) {
            continue;
          }
          if (removed?.previewUrl) {
            URL.revokeObjectURL(removed.previewUrl);
            previewUrlsRef.current.delete(removed.previewUrl);
          }
          previewCacheRef.current.delete(idToRemove);
          if (storage) {
            try {
              storage.removeItem(`${CACHE_STORAGE_PREFIX}${idToRemove}`);
            } catch (storageError) {
              console.warn(`Impossibile eliminare la cache dell'anteprima ${idToRemove}`, storageError);
            }
          }
        }
      }
    } catch (cacheError) {
      console.warn('Impossibile ripristinare la cache delle anteprime', cacheError);
      if (storage) {
        const keysToRemove = [];
        for (let index = 0; index < storage.length; index += 1) {
          const key = storage.key(index);
          if (key?.startsWith(CACHE_STORAGE_PREFIX)) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach((key) => storage.removeItem(key));
      }
      previewCacheRef.current.clear();
    }
    cacheLoadedRef.current = true;
  }

  const removeCacheEntry = useCallback(
    (id) => {
      const storage = getPreviewStorage();
      if (!storage) {
        return;
      }
      const storageKey = `${CACHE_STORAGE_PREFIX}${id}`;
      try {
        storage.removeItem(storageKey);
      } catch (storageError) {
        console.warn(`Impossibile eliminare la cache dell'anteprima ${id}`, storageError);
      }
    },
    [getPreviewStorage]
  );

  const clearCacheStorage = useCallback(() => {
    const storage = getPreviewStorage();
    if (!storage) {
      return;
    }
    const keysToRemove = [];
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (key?.startsWith(CACHE_STORAGE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => {
      try {
        storage.removeItem(key);
      } catch (storageError) {
        console.warn(`Impossibile eliminare la cache dell'anteprima ${key}`, storageError);
      }
    });
  }, [getPreviewStorage]);

  const isQuotaExceededError = (error) => {
    if (!error) return false;
    return error.code === 22 || error.name === 'QuotaExceededError' || error.name === 'NS_ERROR_DOM_QUOTA_REACHED';
  };

  const evictOldestCacheEntry = useCallback(
    (excludeId) => {
      let oldestId = null;
      let oldestTimestamp = Number.POSITIVE_INFINITY;
      const cache = previewCacheRef.current;

      cache.forEach((cached, key) => {
        if (key === excludeId) {
          return;
        }
        const storedAt = cached?.storedAt ?? 0;
        if (storedAt < oldestTimestamp) {
          oldestId = key;
          oldestTimestamp = storedAt;
        }
      });

      if (!oldestId) {
        return false;
      }

      const removed = cache.get(oldestId);
      if (removed?.previewUrl) {
        URL.revokeObjectURL(removed.previewUrl);
        previewUrlsRef.current.delete(removed.previewUrl);
      }
      cache.delete(oldestId);
      removeCacheEntry(oldestId);
      return true;
    },
    [removeCacheEntry]
  );

  const persistCacheEntry = useCallback(
    (id, entry) => {
      const storage = getPreviewStorage();
      if (!storage) {
        return;
      }
      const storageKey = `${CACHE_STORAGE_PREFIX}${id}`;
      if (!entry?.dataUrl) {
        try {
          storage.removeItem(storageKey);
        } catch (storageError) {
          console.warn(`Impossibile rimuovere la cache dell'anteprima ${id}`, storageError);
        }
        return;
      }

      const payload = {
        dataUrl: entry.dataUrl,
        mimeType: entry.mimeType,
        fileName: entry.fileName,
        storedAt: entry.storedAt,
      };

      try {
        storage.setItem(storageKey, JSON.stringify(payload));
      } catch (storageError) {
        if (isQuotaExceededError(storageError)) {
          const evicted = evictOldestCacheEntry(id);
          if (evicted) {
            try {
              storage.setItem(storageKey, JSON.stringify(payload));
              return;
            } catch (retryError) {
              console.warn(`Impossibile salvare la cache dell'anteprima ${id} dopo l'evict`, retryError);
            }
          }
        }
        console.warn(`Impossibile salvare la cache dell'anteprima ${id}`, storageError);
      }
    },
    [getPreviewStorage, evictOldestCacheEntry]
  );

  const ensureCacheWithinLimit = useCallback(
    (excludeId) => {
      const cache = previewCacheRef.current;
      while (cache.size > MAX_CACHED_PREVIEWS) {
        if (!evictOldestCacheEntry(excludeId)) {
          break;
        }
      }
    },
    [evictOldestCacheEntry]
  );

  const {
    searchQuery,
    page,
    statusFilter,
    handleSearchChange,
    setPage,
    setStatusFilter,
    clearSearch,
    buildFilters,
  } = useListState({
    defaults: {
      search: '',
      status: '',
      page: 0,
      size: DEFAULT_PAGE_SIZE,
      sortBy: 'createdAt',
      sortOrder: 'desc',
    },
    options: {
      searchDebounceMs: 400,
    },
  });

  // --- GESTIONE PERSISTENZA FILTRI (sessionStorage) ---
  useEffect(() => {
    const savedFilters = sessionStorage.getItem("documentiFilters");
    if (savedFilters) {
      try {
        const { search, status, type, inAttributes } = JSON.parse(savedFilters);
        if (search) handleSearchChange({ target: { value: search } });
        if (status) setStatusFilter(status);
        if (type) setTypeFilter(type);
        if (inAttributes) setSearchInAttributes(true);
      } catch (err) {
        console.warn("Impossibile leggere i filtri salvati", err);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ogni volta che cambiano, li salvo
  useEffect(() => {
    const filters = {
      search: searchQuery || "",
      status: statusFilter || "",
      type: typeFilter || "",
      inAttributes: searchInAttributes
    };
    sessionStorage.setItem("documentiFilters", JSON.stringify(filters));
  }, [searchQuery, statusFilter, typeFilter, searchInAttributes]);
    

  const revokePreviewUrls = useCallback(() => {
    previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    previewUrlsRef.current.clear();
  }, []);

  const resetAndReload = useCallback(() => {
    setHasMore(true);
    setIsFetchingMore(false);
    previewGenerationRef.current = Date.now();

    previewCacheRef.current.forEach((cached) => {
      if (cached?.previewUrl) {
        URL.revokeObjectURL(cached.previewUrl);
      }
    });
    previewCacheRef.current.clear();
    clearCacheStorage();

    revokePreviewUrls();

    if (page !== 0) {
      setPage(0);
    } else {
      setRefreshKey((value) => value + 1);
    }
  }, [page, setPage, revokePreviewUrls, clearCacheStorage]);

  const pruneCache = useCallback(
    (activeIds) => {
      const cache = previewCacheRef.current;
      cache.forEach((cached, key) => {
        if (!activeIds.has(key)) {
          if (cached?.previewUrl) {
            URL.revokeObjectURL(cached.previewUrl);
            previewUrlsRef.current.delete(cached.previewUrl);
          }
          cache.delete(key);
          removeCacheEntry(key);
        }
      });
    },
    [removeCacheEntry]
  );

  const updateCardPreview = useCallback(
    (codexId, nextData, cacheEntry) => {
      setCodexCards((prevCards) =>
        prevCards.map((card) => {
          if (card.id !== codexId) {
            return card;
          }

          const updatedCard = {
            ...card,
            ...nextData,
          };

          if (card.previewUrl && card.previewUrl !== updatedCard.previewUrl) {
            URL.revokeObjectURL(card.previewUrl);
            previewUrlsRef.current.delete(card.previewUrl);
          }

          if (updatedCard.previewUrl) {
            previewUrlsRef.current.add(updatedCard.previewUrl);
          }

          const entryWithMeta = cacheEntry
            ? { ...cacheEntry, storedAt: cacheEntry.storedAt ?? Date.now() }
            : null;

          if (entryWithMeta) {
            previewCacheRef.current.set(codexId, entryWithMeta);
            persistCacheEntry(codexId, entryWithMeta);
            ensureCacheWithinLimit(codexId);
          } else {
            if (previewCacheRef.current.delete(codexId)) {
              removeCacheEntry(codexId);
            }
          }

          return updatedCard;
        })
      );
    },
    [persistCacheEntry, removeCacheEntry, ensureCacheWithinLimit]
  );

  const loadCardPreview = useCallback(
    async (codexId, generationId) => {
      try {
        // Always reload thumbnail from API (no caching)
        const thumbnailResponse = await codexService.getThumbnail(codexId);
        
        if (previewGenerationRef.current !== generationId) {
          return;
        }

        // Check if thumbnail exists (null = 404, no thumbnail available)
        if (!thumbnailResponse || !thumbnailResponse.thumbnail) {
          // Silently handle missing thumbnail
          updateCardPreview(codexId, {
            previewUrl: null,
            previewMimeType: null,
            previewFileName: null,
            previewLoading: false,
          }, null);
          return;
        }
        
        // Use the base64 data URL directly
        updateCardPreview(
          codexId,
          {
            previewUrl: thumbnailResponse.thumbnail,
            previewMimeType: 'image/jpeg',
            previewFileName: 'thumbnail.jpg',
            previewLoading: false,
          },
          null // Don't cache - always reload
        );
      } catch (innerError) {
        // Only log real errors (not 404s which return null above)
        console.error(`Errore nel caricamento dell'anteprima per il Codex ${codexId}`, innerError);
        if (previewGenerationRef.current !== generationId) {
          return;
        }
        updateCardPreview(
          codexId,
          {
            previewUrl: null,
            previewMimeType: null,
            previewFileName: null,
            previewLoading: false,
          },
          null
        );
      }
    },
    [updateCardPreview]
  );

  useEffect(() => {
    const loadCodexTypes = async () => {
      try {
        const types = await codexTypeService.getAll();
        setCodexTypes(types);
      } catch (err) {
        console.error('Errore nel caricamento dei tipi di Codex:', err);
      }
    };

    loadCodexTypes();
  }, []);

  useEffect(() => {
    let isMounted = true;

    const generationId = page === 0 ? Date.now() : previewGenerationRef.current;
    if (page === 0) {
      previewGenerationRef.current = generationId;
    }

    const fetchCodexes = async () => {
      const isFirstPage = page === 0;
      try {
        if (isFirstPage) {
          setLoading(true);
        }
        setError(null);

        const filters = {
          ...buildFilters(),
          codexTypeId: typeFilter || undefined,
          searchInAttributes: searchInAttributes || undefined,
          sort: 'createdAt,desc',
        };

        const response = await codexService.getAll(filters);
        if (!isMounted) {
          return;
        }

        const content = response.content || [];
        const total = response.totalElements || 0;
        const cardsToLoad = [];
        
        // Always reload thumbnails - no caching
        const baseCards = content.map((codex) => {
          cardsToLoad.push(codex.id);
          return {
            ...codex,
            previewUrl: null,
            previewMimeType: null,
            previewFileName: null,
            previewLoading: true,
          };
        });

        setTotalElements(total);
        setHasMore(!response.last);

        if (isFirstPage) {
          const incomingIds = new Set(content.map((codex) => codex.id));
          pruneCache(incomingIds);

          setCodexCards(baseCards);
          cardsToLoad.forEach((id) => loadCardPreview(id, generationId));
        } else {
          setCodexCards((prevCards) => {
            const existingIds = new Set(prevCards.map((card) => card.id));
            const cardsToAdd = [];

            baseCards.forEach((card) => {
              if (existingIds.has(card.id)) {
                return;
              }
              // Always load thumbnail - no cache check
              cardsToAdd.push(card);
            });

            if (cardsToAdd.length === 0) {
              return prevCards;
            }

            cardsToLoad.forEach((id) => loadCardPreview(id, generationId));
            return [...prevCards, ...cardsToAdd];
          });
        }
      } catch (err) {
        console.error('Errore nel caricamento dei Codex:', err);
        if (!isMounted) {
          return;
        }
        setError('Impossibile caricare i documenti.');
        if (page === 0) {
          previewCacheRef.current.forEach((cached) => {
            if (cached?.previewUrl) {
              URL.revokeObjectURL(cached.previewUrl);
            }
          });
          previewCacheRef.current.clear();
          clearCacheStorage();
          revokePreviewUrls();
          setCodexCards([]);
          setTotalElements(0);
        }
        setHasMore(false);
      } finally {
        if (isMounted) {
          if (page === 0) {
            setLoading(false);
          } else {
            setIsFetchingMore(false);
          }
          setIsSearching(false);
        }
      }
    };

    fetchCodexes();

    return () => {
      isMounted = false;
    };
  }, [
    buildFilters,
    typeFilter,
    searchInAttributes,
    page,
    refreshKey,
    loadCardPreview,
    pruneCache,
    revokePreviewUrls,
    getPreviewStorage,
    ensureCacheWithinLimit,
  ]);

  useEffect(() => () => revokePreviewUrls(), [revokePreviewUrls]);

  const handleLoadMore = useCallback(() => {
    if (loading || isFetchingMore || !hasMore) {
      return;
    }
    setIsFetchingMore(true);
    setPage(page + 1);
  }, [loading, isFetchingMore, hasMore, page, setPage]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        sentinelVisibleRef.current = entry.isIntersecting;
        if (entry.isIntersecting) {
          handleLoadMore();
        }
      },
      { root: null, rootMargin: '200px 0px', threshold: 0 }
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [handleLoadMore]);

  useEffect(() => {
    if (!loading && !isFetchingMore && hasMore && sentinelVisibleRef.current) {
      handleLoadMore();
    }
  }, [loading, isFetchingMore, hasMore, handleLoadMore]);

  useEffect(() => {
    if (pollingCancelRef.current) {
      pollingCancelRef.current();
      pollingCancelRef.current = null;
    }

    let cancelled = false;

    const runPolling = async () => {
      if (loadingStateRef.current) {
        return;
      }

      try {
        const filters = {
          ...buildFilters(),
          codexTypeId: typeFilter || undefined,
          searchInAttributes: searchInAttributes || undefined,
          sort: 'createdAt,desc',
          page: 0,
          size: DEFAULT_PAGE_SIZE,
        };

        const response = await codexService.getAll(filters);
        if (cancelled) {
          return;
        }

        const latestCards = response.content || [];
        const idsToLoad = [];
        const generationId = previewGenerationRef.current;

        setCodexCards((prevCards) => {
          if (latestCards.length === 0 && prevCards.length === 0) {
            return prevCards;
          }

          const existingMap = new Map(prevCards.map((card) => [card.id, card]));
          const updatedSegment = latestCards.map((codex) => {
            const existing = existingMap.get(codex.id);
            if (existing) {
              existingMap.delete(codex.id);
              return { ...existing, ...codex };
            }

            idsToLoad.push(codex.id);
            return {
              ...codex,
              previewUrl: null,
              previewMimeType: null,
              previewFileName: null,
              previewLoading: true,
            };
          });

          const remainingCards = Array.from(existingMap.values());
          const nextCards = [...updatedSegment, ...remainingCards];

          const hasChanges =
            nextCards.length !== prevCards.length ||
            nextCards.some((card, index) => {
              const previous = prevCards[index];
              if (!previous || previous.id !== card.id) {
                return true;
              }
              return (
                previous.status !== card.status ||
                previous.description !== card.description ||
                previous.code !== card.code
              );
            });

          if (!hasChanges) {
            idsToLoad.length = 0;
            return prevCards;
          }

          return nextCards;
        });

        if (idsToLoad.length > 0) {
          idsToLoad.forEach((id) => {
            loadCardPreview(id, generationId);
          });
        }

        setTotalElements(response.totalElements || 0);
        setHasMore(!response.last);
      } catch (pollError) {
        if (!cancelled) {
          console.error('Errore durante l\'aggiornamento automatico dei documenti:', pollError);
        }
      }
    };

    const intervalId = setInterval(runPolling, 5000);
    pollingCancelRef.current = () => {
      cancelled = true;
      clearInterval(intervalId);
    };

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [buildFilters, typeFilter, searchInAttributes, statusFilter, searchQuery, loadCardPreview]);

  const handleTypeChange = (event) => {
    setTypeFilter(event.target.value);
    setPage(0); // resettare la pagina
  };

  const handleStatusChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const handleSearchChangeWithLoading = useCallback(
    (event) => {
      if (!event || !event.target) return;
      setIsSearching(true);
      handleSearchChange(event);
      resetAndReload();
      setTimeout(() => {
        setIsSearching(false);
      }, 500);
    },
    [handleSearchChange, resetAndReload]
  );

  const handleClearSearch = useCallback(() => {
    clearSearch();
    resetAndReload();
  }, [clearSearch, resetAndReload]);

  const shouldShowAddButton = can('codex.create');

  const handleCardOpen = (codexId) => {
    navigate(`/codex/${codexId}`);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Documenti</Typography>
        {shouldShowAddButton && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setIsCreateOpen(true)}
          >
            Nuovo documento
          </Button>
        )}
      </Box>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="Cerca per codice o descrizione..."
              value={searchQuery}
              onChange={handleSearchChangeWithLoading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {isSearching ? (
                      <CircularProgress size={20} />
                    ) : searchQuery ? (
                      <IconButton size="small" onClick={handleClearSearch}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    ) : null}
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={searchInAttributes}
                  onChange={(e) => setSearchInAttributes(e.target.checked)}
                  size="small"
                />
              }
              label="Cerca in attributi"
              sx={{ ml: 0 }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Stato</InputLabel>
              <Select
                value={statusFilter}
                label="Stato"
                onChange={handleStatusChange}
                renderValue={(selected) => {
                  if (!selected) return 'Tutti';
                  const statusConfig = getStatusConfig('CODEX', selected);
                  return statusConfig.label;
                }}
              >
                <MenuItem value="">Tutti</MenuItem>
                {Object.entries(STATUS_CONFIG.CODEX).map(([status, config]) => (
                  <MenuItem key={status} value={status}>
                    {config.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Tipo</InputLabel>
              <Select
                value={typeFilter}
                label="Tipo"
                onChange={handleTypeChange}
                renderValue={(selected) => {
                  if (!selected) return 'Tutti';
                  const selectedType = codexTypes.find((type) => type.id === selected);
                  if (!selectedType) return selected;
                  return selectedType.description
                    ? `${selectedType.code} - ${selectedType.description}`
                    : selectedType.code;
                }}
              >
                <MenuItem value="">Tutti</MenuItem>
                {[...codexTypes]
                  .sort((a, b) => (a.code || '').localeCompare(b.code || ''))
                  .map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.description ? `${type.code} - ${type.description}` : type.code}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
            <Tooltip title="Pulisci filtri">
              <IconButton
                size="small"
                onClick={() => {
                  clearSearch();           // reset ricerca
                  setStatusFilter('');      // reset stato
                  setTypeFilter('');        // reset tipo
                  setSearchInAttributes(false); // reset ricerca attributi
                  sessionStorage.removeItem("documentiFilters");
                  setPage(0);               // reset pagina
                }}
                sx={{
                  color:
                    searchQuery || statusFilter || typeFilter
                      ? 'primary.main'  // colore attivo quando c'è almeno un filtro
                      : 'action.disabled', // colore neutro quando non ci sono filtri
                }}
              >
                <FilterAltOffOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </Paper>

      {loading && codexCards.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 240 }}>
          <CircularProgress />
        </Box>
      ) : codexCards.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
          <Typography variant="subtitle1">Nessun documento disponibile.</Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {codexCards.map((codex) => (
            <Grid item xs={12} sm={6} md={3} lg={2} key={codex.id}>
              <CodexThumbnailCard
                codex={codex}
                onOpen={() => handleCardOpen(codex.id)}
                isLoadingPreview={Boolean(codex.previewLoading)}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <Box ref={sentinelRef} sx={{ height: hasMore ? 24 : 0 }} />

      {isFetchingMore && !loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      <DocumentCreateDialog
        open={isCreateOpen}
        onClose={() => {
          setIsCreateOpen(false);
          setError(null);
        }}
        onCreated={() => {
          setIsCreateOpen(false);
          setSuccessMessage('Documento creato correttamente.');
          setError(null);
          resetAndReload();
        }}
        codexTypes={codexTypes}
      />
    </Box>
  );
};

export default Documenti;
